import { useState } from 'react';
import { FirebaseError } from 'firebase/app';
import { SolidButton } from '../Button';
import CustomInput from '../CustomInput';
import { P } from '../Text';
import { useTranslation } from 'next-i18next/pages';
import {
  confirmPasswordReset,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { auth } from 'config/initAuth';

interface Props {
  closeModal: () => void;
}

interface IPasswordResetFormIF {
  password: string;
  passwordRe: string;
}

const PasswordResetForm: React.FC<Props> = (props: Props) => {
  const { closeModal } = props;
  const [formState, setFormState] = useState<IPasswordResetFormIF>({
    password: '',
    passwordRe: '',
  });
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleChange = ({
    target: { name, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = () => {
    if (!(formState.password && formState.passwordRe)) return;
    if (formState.password !== formState.passwordRe) {
      setError(t('Passwords are not matching'));
      return;
    }
    const url = window.location.search;
    const oobCodeIndex = url.indexOf('oobCode=');
    if (oobCodeIndex == -1) return;

    const code = url.substring(
      url.indexOf('=', oobCodeIndex) + 1,
      url.indexOf('&', oobCodeIndex),
    );
    verifyPasswordResetCode(auth, code)
      .then(email => {
        confirmPasswordReset(auth, code, formState.password)
          .then(function () {
            setError(null);
            closeModal();
            signInWithEmailAndPassword(auth, email, formState.password);
          })
          .catch(function (err: FirebaseError) {
            switch (err.code) {
              case 'auth/expired-action-code':
                setError(t('Link was expired'));
                break;
              case 'auth/invalid-action-code':
                setError(t('Invalid action code was sent'));
                break;
              case 'auth/user-disabled':
                setError(t('User is disabled'));
                break;
              case 'auth/user-not-found':
                setError(t('User not found'));
                break;
              case 'auth/weak-password':
                setError(t('Weak password'));
                break;
              default:
                setError(`Error with code: ${err.code})`);
                break;
            }
          });
      })
      .catch(function (err: FirebaseError) {
        switch (err.code) {
          case 'auth/expired-action-code':
            setError(t('Expired password reset link'));
            break;
          case 'auth/invalid-action-code':
            setError(t('Invalid action code'));
            break;
          default:
            setError(`Error with code: ${err.code})`);
            break;
        }
      });
  };

  const onFormSubmit = (e: any) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <form
      className="flex flex-col space-y-4 mt-4 w-full md:w-4/5"
      onSubmit={onFormSubmit}>
      {error && <P className="text-red-600">{error}</P>}
      <CustomInput
        value={formState.password}
        onChange={handleChange}
        onEnter={() => {}}
        placeholder={t('Password') || ''}
        type="password"
        name="password"
        containerClassName="h-12"
      />
      <CustomInput
        value={formState.passwordRe}
        onChange={handleChange}
        onEnter={() => {}}
        placeholder={t('Password again') || ''}
        type="password"
        name="passwordRe"
        containerClassName="h-12"
      />
      <SolidButton
        buttonType="submit"
        className="w-full sm:w-3/5 self-center">
        {t('Reset password')}
      </SolidButton>
    </form>
  );
};

export default PasswordResetForm;
